import type {
	LocalDecorationId,
	ValueDecoration,
} from '@jira/polaris__domain-field/src/ValueDecoration.types.tsx';
import { generateLocalDecorationId } from '@jira/polaris__domain-field/src/generateLocalDecorationId.tsx';
import type { FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import { fireCompoundAnalyticsEvent } from '@jira/polaris__lib-analytics/src/fireCompoundAnalyticsEvent.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { Props, State } from './FieldsUpdate.types.tsx';
import { performJiraConfigurationUpdate } from './performJiraConfigurationUpdate.tsx';

type CreateValueDecorationShared = {
	fieldKey: FieldKey;
};

type CreateValueDecorationProps = CreateValueDecorationShared &
	Omit<ValueDecoration, 'localDecorationId'>;

type CreateValueDecorationBulkProps = CreateValueDecorationShared & {
	decorations: Omit<ValueDecoration, 'localDecorationId'>[];
};

export const createValueDecoration =
	(createProps: CreateValueDecorationProps) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote, onDecorationUpdateFailed, createAnalyticsEvent }: Props,
	): Promise<LocalDecorationId> => {
		const { fieldKey, backgroundColor, highlightContainer, emoji, logic } = createProps;
		const state = getState();
		const { fieldValueDecorations, fields } = state;
		const field = fields[fieldKey];
		const existingValueDecorations = fieldValueDecorations[fieldKey] || [];

		const localDecorationId = generateLocalDecorationId();
		const newValueDecorations = [...existingValueDecorations];

		newValueDecorations.push({
			localDecorationId,
			backgroundColor,
			emoji,
			highlightContainer,
			logic,
		});

		setState({
			fieldValueDecorations: {
				...fieldValueDecorations,
				[fieldKey]: newValueDecorations,
			},
		});

		try {
			await performJiraConfigurationUpdate({
				fieldKey,
				fieldRemote,
				existingDecorations: existingValueDecorations,
				newDecorations: newValueDecorations,
			});

			fireCompoundAnalyticsEvent.FieldConfig.valueDecorationChanged(createAnalyticsEvent({}), {
				issueFieldKey: fieldKey,
				issueFieldType: field.type,
			});
		} catch (error) {
			setState({
				fieldValueDecorations: {
					...getState().fieldValueDecorations,
					[fieldKey]: existingValueDecorations,
				},
			});

			onDecorationUpdateFailed(
				error instanceof Error ? error : new Error('Failed to create value decoration'),
			);
		}

		return localDecorationId;
	};

export const createValueDecorationBulk =
	(createProps: CreateValueDecorationBulkProps) =>
	(stateParams: StoreActionApi<State>, propParams: Props): LocalDecorationId[] => {
		const { getState, setState } = stateParams;
		const { fieldRemote, onDecorationUpdateFailed, createAnalyticsEvent } = propParams;

		const { fieldKey, decorations } = createProps;
		const state = getState();
		const { fieldValueDecorations, fields } = state;
		const field = fields[fieldKey];

		const existingValueDecorations = fieldValueDecorations[fieldKey] || [];
		const newValueDecorations = [...existingValueDecorations];
		const localDecorationIds: string[] = [];

		decorations.forEach((decoration) => {
			const { backgroundColor, highlightContainer, emoji, logic } = decoration;
			const localDecorationId = generateLocalDecorationId();

			localDecorationIds.push(localDecorationId);
			newValueDecorations.push({
				localDecorationId,
				backgroundColor,
				emoji,
				highlightContainer,
				logic,
			});
		});

		performJiraConfigurationUpdate({
			fieldKey,
			fieldRemote,
			existingDecorations: existingValueDecorations,
			newDecorations: newValueDecorations,
		}).catch((error: Error) => {
			onDecorationUpdateFailed(error);
		});

		setState({
			fieldValueDecorations: {
				...fieldValueDecorations,
				[fieldKey]: newValueDecorations,
			},
		});

		fireCompoundAnalyticsEvent.FieldConfig.valueDecorationChanged(createAnalyticsEvent({}), {
			issueFieldKey: fieldKey,
			issueFieldType: field.type,
		});

		return localDecorationIds;
	};
